/**
 * CodingStructurePage - LGL Coding Structure Reference
 *
 * Displays the complete Maestro coding structure from verified source files.
 * 6 tabs: Operation Codes, Templates, Discipline Map, Department Codes, Project Structure, Examples
 * Plus a comprehensive Help modal.
 *
 * Data source: lgltools-react/src/data/codingStructureData.js
 */

import React, { useState, useMemo } from 'react';
import {
  OPERATION_CODES, TEMPLATES, DISCIPLINES, COST_TYPES,
  CODE_GROUPS, TM_CODES, DEPARTMENT_CODES, PROJECT_EXAMPLES,
  getCodesForTemplate,
} from '../../data/codingStructureData';
import { ListenButton } from '../common/AudioGuide';
import HelpWithVideoTour from '../Help/HelpWithVideoTour';
import { usePresence, ActiveViewers } from '../common/ViewingTemplate';

// ─── SHARED STYLES ───

const card = {
  backgroundColor: 'white',
  borderRadius: '12px',
  padding: '1.5rem',
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  marginBottom: '1rem',
};

const chip = (bg, fg) => ({
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: '12px',
  fontSize: '0.75rem',
  fontWeight: 600,
  backgroundColor: bg,
  color: fg || '#fff',
  marginRight: '4px',
});

const mono = { fontFamily: "'Consolas', 'Monaco', monospace", fontWeight: 'bold' };

const pillBtn = (active) => ({
  padding: '10px 20px',
  borderRadius: '8px',
  border: 'none',
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: '0.95rem',
  backgroundColor: active ? '#dc3545' : '#f8f9fa',
  color: active ? 'white' : '#333',
  transition: 'all 0.15s',
});

// ─── COLLAPSIBLE SECTION ───

const CollapsibleSection = ({ title, summary, defaultOpen = false, children, accentColor }) => {
  const [open, setOpen] = useState(defaultOpen);
  const color = accentColor || '#495057';

  return (
    <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
      <div
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.75rem',
          padding: '12px 16px', cursor: 'pointer', userSelect: 'none',
          backgroundColor: open ? 'white' : '#fafafa',
          borderBottom: open ? '1px solid #e9ecef' : 'none',
          transition: 'background-color 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f0f4ff'; }}
        onMouseLeave={e => { e.currentTarget.style.backgroundColor = open ? 'white' : '#fafafa'; }}
      >
        <span style={{
          width: '20px', height: '20px', borderRadius: '4px',
          backgroundColor: color + '12', color: color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.7rem', flexShrink: 0, transition: 'transform 0.2s',
          transform: open ? 'rotate(0)' : 'rotate(-90deg)',
        }}>
          ▾
        </span>
        <span style={{ fontWeight: 700, color: '#212529', fontSize: '0.95rem', flex: 1 }}>{title}</span>
        {!open && summary && (
          <span style={{ fontSize: '0.78rem', color: '#6c757d', fontStyle: 'italic', textAlign: 'right', maxWidth: '50%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {summary}
          </span>
        )}
      </div>
      {open && (
        <div style={{ padding: '1rem 1.25rem' }}>{children}</div>
      )}
    </div>
  );
};

// ─── TAB 1: OPERATION CODES (with tree lines) ───

const OperationCodesTab = ({ search }) => {
  const [collapsed, setCollapsed] = useState(() => {
    const init = {};
    CODE_GROUPS.forEach(g => { init[g.id] = true; });
    return init;
  });

  const groups = useMemo(() => {
    const result = {};
    CODE_GROUPS.forEach(g => { result[g.id] = { ...g, codes: [] }; });
    OPERATION_CODES.forEach(c => {
      if (result[c.group]) result[c.group].codes.push(c);
    });
    return Object.values(result).filter(g => g.codes.length > 0);
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return null;
    const q = search.toLowerCase();
    return OPERATION_CODES.filter(
      c => c.code.includes(q) || c.description.toLowerCase().includes(q)
    );
  }, [search]);

  const toggleGroup = (id) => setCollapsed(prev => ({ ...prev, [id]: !prev[id] }));

  const renderCode = (c, idx, arr) => {
    const isPhase = c.type === 'Phase' || c.type === 'Section';
    const ct = c.costType && COST_TYPES[c.costType];
    const isLast = idx === arr.length - 1;
    const nextIsPhase = !isLast && (arr[idx + 1].type === 'Phase' || arr[idx + 1].type === 'Section');

    return (
      <div
        key={c.code + c.group + idx}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: isPhase ? '8px 14px' : '5px 14px',
          paddingLeft: isPhase ? '14px' : '16px',
          backgroundColor: isPhase ? '#f8f9fa' : 'transparent',
          borderBottom: (isLast || nextIsPhase) ? 'none' : '1px solid #f5f5f5',
          marginBottom: isPhase && idx > 0 ? '2px' : 0,
          borderTop: isPhase && idx > 0 ? '1px solid #e9ecef' : 'none',
        }}
      >
        {!isPhase && (
          <span style={{ color: '#d0d0d0', fontSize: '0.75rem', width: '16px', textAlign: 'center', flexShrink: 0 }}>
            {isLast || nextIsPhase ? '└' : '├'}
          </span>
        )}
        {isPhase && <span style={{ width: '16px', flexShrink: 0 }} />}

        <span style={{
          ...mono, width: '56px', flexShrink: 0,
          fontSize: isPhase ? '0.9rem' : '0.8rem',
          color: isPhase ? '#212529' : '#6c757d',
        }}>{c.code}</span>

        <span style={{
          flex: 1, fontSize: isPhase ? '0.9rem' : '0.85rem',
          color: isPhase ? '#212529' : '#495057',
          fontWeight: isPhase ? 700 : 400,
          letterSpacing: isPhase ? '0.02em' : 0,
        }}>{c.description}</span>

        {ct && (
          <span style={{ ...chip(ct.color + '18', ct.color), fontSize: '0.7rem', padding: '1px 7px' }}>
            {ct.letter}
          </span>
        )}

        <span style={{
          fontSize: '0.65rem', color: '#c0c0c0', width: '48px', textAlign: 'right', flexShrink: 0,
          fontFamily: 'monospace',
        }}>{c.templates.join(',')}</span>
      </div>
    );
  };

  return (
    <div>
      {filtered ? (
        <div style={{ ...card, padding: '0.75rem' }}>
          <div style={{ fontSize: '0.8rem', color: '#6c757d', marginBottom: '0.5rem', padding: '0 8px' }}>
            {filtered.length} result{filtered.length !== 1 ? 's' : ''}
          </div>
          {filtered.map((c, i, a) => renderCode(c, i, a))}
          {filtered.length === 0 && <div style={{ padding: '2rem', color: '#adb5bd', textAlign: 'center' }}>No matching codes</div>}
        </div>
      ) : (
        groups.map(g => (
          <div key={g.id} style={{ ...card, padding: 0, overflow: 'hidden' }}>
            <div
              onClick={() => toggleGroup(g.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem',
                padding: '11px 16px', cursor: 'pointer', backgroundColor: g.color + '0a',
                borderLeft: `4px solid ${g.color}`, userSelect: 'none',
                transition: 'background-color 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = g.color + '14'; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = g.color + '0a'; }}
            >
              <span style={{ fontSize: '1.1rem' }}>{g.icon}</span>
              <span style={{ fontWeight: 700, flex: 1, color: '#212529', fontSize: '0.95rem' }}>{g.id}</span>
              <span style={{ ...mono, fontSize: '0.75rem', color: g.color, opacity: 0.8 }}>{g.range}</span>
              <span style={{ fontSize: '0.75rem', color: '#6c757d', backgroundColor: '#e9ecef', padding: '1px 8px', borderRadius: '10px' }}>
                {g.codes.length}
              </span>
              <span style={{ fontSize: '0.7rem', color: '#adb5bd', transition: 'transform 0.2s', transform: collapsed[g.id] ? 'rotate(-90deg)' : 'rotate(0)' }}>▾</span>
            </div>
            {!collapsed[g.id] && (
              <div style={{ padding: '4px 0' }}>
                {g.codes.map((c, i, a) => renderCode(c, i, a))}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
};

// ─── TAB 2: TEMPLATES (sticky header + collapsible) ───

const TemplatesTab = () => {
  const templateColors = ['#7c3aed', '#0891b2', '#ea580c', '#059669'];

  const templateCodes = useMemo(() => {
    const map = {};
    TEMPLATES.forEach(t => {
      const set = new Set(getCodesForTemplate(t.id).map(c => c.code));
      map[t.id] = set;
    });
    return map;
  }, []);

  const downloadTemplate = (file) => {
    const link = document.createElement('a');
    link.href = `/uploads/coding-structure/${file}`;
    link.download = file;
    link.click();
  };

  return (
    <div>
      <CollapsibleSection
        title="Template Overview"
        summary={`${TEMPLATES.length} templates — Large MD, Small MD, Pipe Fab, Tank Shop`}
        accentColor="#7c3aed"
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '0.75rem' }}>
          {TEMPLATES.map((t, i) => (
            <div key={t.id} style={{
              backgroundColor: '#fafafa', borderRadius: '10px', padding: '1.25rem',
              borderLeft: `4px solid ${templateColors[i]}`,
              display: 'flex', flexDirection: 'column', gap: '0.5rem',
            }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
                <span style={{ ...mono, color: templateColors[i], fontSize: '0.85rem' }}>#{t.id}</span>
                <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#212529' }}>{t.name}</span>
              </div>
              <div style={{ fontSize: '0.8rem', color: '#6c757d', flex: 1 }}>{t.description}</div>
              <div style={{ fontSize: '0.7rem', color: '#adb5bd' }}>Sheets: {t.sheets.join(' · ')}</div>
              <button
                onClick={() => downloadTemplate(t.file)}
                style={{
                  padding: '7px 12px', backgroundColor: templateColors[i] + '10', border: `1px solid ${templateColors[i]}30`,
                  borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem',
                  color: templateColors[i], transition: 'all 0.15s', marginTop: '4px',
                }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = templateColors[i] + '20'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = templateColors[i] + '10'; }}
              >
                Download .xlsx
              </button>
            </div>
          ))}
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Code Comparison Matrix"
        summary={`${OPERATION_CODES.length} codes across ${TEMPLATES.length} templates`}
        accentColor="#0891b2"
      >
        <div style={{ overflowX: 'auto', maxHeight: '60vh', borderRadius: '8px', border: '1px solid #e9ecef' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 2 }}>
                <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', position: 'sticky', top: 0, backgroundColor: '#f8f9fa' }}>Code</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', position: 'sticky', top: 0, backgroundColor: '#f8f9fa' }}>Description</th>
                {TEMPLATES.map((t, i) => (
                  <th key={t.id} style={{
                    padding: '10px 6px', textAlign: 'center', borderBottom: `2px solid ${templateColors[i]}`,
                    position: 'sticky', top: 0, backgroundColor: '#f8f9fa', minWidth: '60px',
                    fontSize: '0.75rem', color: templateColors[i], fontWeight: 700,
                  }}>
                    {t.shortName}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {OPERATION_CODES.map((c, idx) => {
                const isPhase = c.type === 'Phase' || c.type === 'Section';
                return (
                  <tr
                    key={c.code + c.group + idx}
                    style={{ backgroundColor: isPhase ? '#f8f9fa' : 'transparent' }}
                    onMouseEnter={e => { if (!isPhase) e.currentTarget.style.backgroundColor = '#fafafa'; }}
                    onMouseLeave={e => { if (!isPhase) e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    <td style={{ padding: '5px 12px', borderBottom: '1px solid #f5f5f5', ...mono, fontSize: '0.78rem', color: '#6c757d' }}>{c.code}</td>
                    <td style={{
                      padding: '5px 12px', borderBottom: '1px solid #f5f5f5',
                      fontWeight: isPhase ? 700 : 400, paddingLeft: isPhase ? '12px' : '28px',
                      color: isPhase ? '#212529' : '#495057', fontSize: isPhase ? '0.82rem' : '0.8rem',
                    }}>{c.description}</td>
                    {TEMPLATES.map((t, i) => (
                      <td key={t.id} style={{ padding: '5px 6px', borderBottom: '1px solid #f5f5f5', textAlign: 'center' }}>
                        {templateCodes[t.id]?.has(c.code) ? (
                          <span style={{ color: templateColors[i], fontWeight: 700, fontSize: '1rem' }}>&#10003;</span>
                        ) : (
                          <span style={{ color: '#e9ecef' }}>·</span>
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CollapsibleSection>
    </div>
  );
};

// ─── TAB 3: DISCIPLINE MAP (collapsible) ───

const DisciplineMapTab = () => (
  <div>
    <CollapsibleSection
      title="9 Disciplines — Code Ranges"
      summary="PIP, MWR, ELE, SHM, PFS, CIV, STR, TNK, BMT — Labour + Extras per discipline"
      accentColor="#0891b2"
    >
      <p style={{ margin: '0 0 1rem 0', color: '#6c757d', fontSize: '0.85rem' }}>
        Each discipline has a Labour Activity range and a matching Extras code. The offset is always +60,000.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {DISCIPLINES.map(d => (
          <div
            key={d.id}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '10px 14px',
              borderRadius: '8px', backgroundColor: '#fafafa', transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = d.color + '0a'; e.currentTarget.style.boxShadow = `inset 3px 0 0 ${d.color}`; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#fafafa'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            <span style={{
              ...mono, fontSize: '0.9rem', color: 'white', backgroundColor: d.color,
              width: '44px', textAlign: 'center', padding: '3px 0', borderRadius: '5px', flexShrink: 0,
            }}>{d.id}</span>
            <span style={{ flex: 1, fontWeight: 600, color: '#333', fontSize: '0.9rem' }}>{d.name}</span>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.6rem', color: '#adb5bd', marginBottom: '1px', letterSpacing: '0.05em' }}>LABOUR</div>
                <span style={{ ...mono, fontSize: '0.8rem', color: '#495057', padding: '2px 8px', backgroundColor: 'white', borderRadius: '4px', border: '1px solid #e9ecef' }}>
                  {d.labourRange}
                </span>
              </div>
              <svg width="20" height="12" viewBox="0 0 20 12" style={{ flexShrink: 0 }}>
                <line x1="0" y1="6" x2="14" y2="6" stroke="#adb5bd" strokeWidth="1.5" />
                <polyline points="12,2 16,6 12,10" fill="none" stroke="#adb5bd" strokeWidth="1.5" />
              </svg>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.6rem', color: '#adb5bd', marginBottom: '1px', letterSpacing: '0.05em' }}>EXTRAS</div>
                <span style={{ ...mono, fontSize: '0.8rem', color: '#856404', padding: '2px 8px', backgroundColor: '#fff3cd', borderRadius: '4px', border: '1px solid #ffe69c' }}>
                  {d.extrasCode}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </CollapsibleSection>

    <CollapsibleSection
      title="The +60,000 Offset Pattern"
      summary="Extras code = Labour phase + 60,000 for every discipline"
      accentColor="#0a58ca"
    >
      <div style={{ backgroundColor: '#f0f4ff', borderRadius: '8px', padding: '1rem', border: '1px solid #c5d5f7' }}>
        <p style={{ margin: 0, color: '#3b6dcc', fontSize: '0.85rem', lineHeight: '1.5' }}>
          Every discipline's Extras code = Labour phase code + 60,000.<br />
          Piping: <strong>21</strong>000 → <strong>81</strong>000 &nbsp;|&nbsp;
          M&R: <strong>22</strong>000 → <strong>82</strong>000 &nbsp;|&nbsp;
          Electrical: <strong>23</strong>000 → <strong>83</strong>000 &nbsp;|&nbsp; … and so on for all 9.
        </p>
      </div>
    </CollapsibleSection>
  </div>
);

// ─── TAB 4: DEPARTMENT CODES (collapsible) ───

const DepartmentCodesTab = () => {
  const [division, setDivision] = useState('canadian');
  const data = DEPARTMENT_CODES[division];

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        {['canadian', 'us'].map(key => (
          <button key={key} onClick={() => setDivision(key)} style={pillBtn(division === key)}>
            {key === 'canadian' ? 'Canadian Sales' : 'US Sales'}
          </button>
        ))}
      </div>

      <CollapsibleSection
        title="Code Format in Maestro"
        summary={`${data.prefix} + ${data.regionLabel} + Customer`}
        accentColor="#0a58ca"
      >
        <div style={{ backgroundColor: '#f0f4ff', borderRadius: '8px', padding: '1rem', border: '1px solid #c5d5f7' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap', marginBottom: '10px' }}>
            <span style={{ ...mono, padding: '8px 16px', borderRadius: '6px', fontSize: '1.15rem', backgroundColor: '#dc3545', color: 'white' }}>
              {data.prefix}
            </span>
            <span style={{ fontSize: '1.5rem', color: '#d0d0d0', fontWeight: 300 }}>+</span>
            <span style={{ ...mono, padding: '8px 16px', borderRadius: '6px', fontSize: '1.15rem', backgroundColor: '#0d6efd', color: 'white' }}>
              XX
            </span>
            <span style={{ fontSize: '1.5rem', color: '#d0d0d0', fontWeight: 300 }}>+</span>
            <span style={{ ...mono, padding: '8px 16px', borderRadius: '6px', fontSize: '1.15rem', backgroundColor: '#28a745', color: 'white' }}>
              CUSTOMER
            </span>
          </div>
          <div style={{ fontSize: '0.8rem', color: '#3b6dcc', lineHeight: '1.5' }}>
            <strong style={{ color: '#dc3545' }}>{data.prefix}</strong> = {data.prefix === 'C' ? 'Canada' : 'United States'} &nbsp;&nbsp;
            <strong style={{ color: '#0d6efd' }}>XX</strong> = 2-letter {data.regionLabel} &nbsp;&nbsp;
            <strong style={{ color: '#28a745' }}>CUSTOMER</strong> = Customer name or category
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title={`${division === 'canadian' ? 'Canadian' : 'US'} Department Codes`}
        summary={`${data.codes.length} codes`}
        accentColor="#28a745"
      >
        <div style={{ borderRadius: '8px', border: '1px solid #e9ecef', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ padding: '10px 14px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '0.75rem', color: '#6c757d', letterSpacing: '0.05em' }}>MAESTRO CODE</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '0.75rem', color: '#6c757d', letterSpacing: '0.05em', width: '60px' }}>{data.regionLabel.toUpperCase()}</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '0.75rem', color: '#6c757d', letterSpacing: '0.05em' }}>CUSTOMER</th>
              </tr>
            </thead>
            <tbody>
              {data.codes.map((c, i) => (
                <tr
                  key={c.code}
                  style={{ backgroundColor: i % 2 === 0 ? 'transparent' : '#fafafa' }}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f0f4ff'; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = i % 2 === 0 ? 'transparent' : '#fafafa'; }}
                >
                  <td style={{ padding: '8px 14px', borderBottom: '1px solid #f5f5f5', ...mono, fontSize: '0.82rem', color: '#495057' }}>{c.code}</td>
                  <td style={{ padding: '8px 14px', borderBottom: '1px solid #f5f5f5' }}>
                    <span style={{ ...chip('#0d6efd15', '#0d6efd'), fontWeight: 700 }}>{c.region}</span>
                  </td>
                  <td style={{ padding: '8px 14px', borderBottom: '1px solid #f5f5f5', color: '#333' }}>{c.customer}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CollapsibleSection>
    </div>
  );
};

// ─── SVG CONNECTOR DIAGRAM ───

const ConnectorDiagram = ({ masterLabel, masterSub, items, masterColor = '#0d6efd' }) => {
  const boxH = 36;
  const gap = 6;
  const totalH = items.length * (boxH + gap) - gap;
  const masterY = totalH / 2;
  const masterW = 140;
  const masterH = 60;
  const lineX = masterW + 30;
  const subX = lineX + 30;
  const subW = 220;
  const svgW = subX + subW + 10;

  return (
    <svg width="100%" viewBox={`0 0 ${svgW} ${Math.max(totalH, masterH) + 20}`} style={{ maxWidth: '600px', display: 'block' }}>
      <rect x="0" y={masterY - masterH / 2 + 10} width={masterW} height={masterH} rx="10" fill={masterColor} />
      <text x={masterW / 2} y={masterY - 4 + 10} textAnchor="middle" fill="white" fontSize="9" opacity="0.8">
        {masterSub}
      </text>
      <text x={masterW / 2} y={masterY + 12 + 10} textAnchor="middle" fill="white" fontSize="14" fontFamily="Consolas, monospace" fontWeight="bold">
        {masterLabel}
      </text>
      <line x1={masterW} y1={masterY + 10} x2={lineX} y2={masterY + 10} stroke="#c0c0c0" strokeWidth="1.5" />
      <line x1={lineX} y1={10 + boxH / 2} x2={lineX} y2={10 + totalH - boxH / 2} stroke="#c0c0c0" strokeWidth="1.5" />
      {items.map((item, i) => {
        const y = 10 + i * (boxH + gap);
        const cy = y + boxH / 2;
        return (
          <g key={item.id}>
            <line x1={lineX} y1={cy} x2={subX} y2={cy} stroke="#c0c0c0" strokeWidth="1.5" />
            <rect x={subX} y={y} width={subW} height={boxH} rx="6" fill={item.color + '12'} stroke={item.color + '40'} strokeWidth="1" />
            <text x={subX + 10} y={cy + 4} fill={item.color} fontSize="12" fontFamily="Consolas, monospace" fontWeight="bold">
              {item.code}
            </text>
            <text x={subX + subW - 8} y={cy + 4} textAnchor="end" fill="#6c757d" fontSize="10">
              {item.name}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

// ─── TAB 5: PROJECT STRUCTURE (collapsible + SVG) ───

const ProjectStructureTab = () => {
  const [view, setView] = useState('multi');

  const multiItems = DISCIPLINES.map(d => ({
    id: d.id,
    code: `#####${d.id}`,
    name: d.name,
    color: d.color,
  }));

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        {[
          { id: 'multi', label: 'Multi-Discipline' },
          { id: 'tm', label: 'Time & Material' },
        ].map(v => (
          <button key={v.id} onClick={() => setView(v.id)} style={pillBtn(view === v.id)}>
            {v.label}
          </button>
        ))}
      </div>

      {view === 'multi' ? (
        <div>
          <CollapsibleSection
            title="Multi-Discipline Project Structure"
            summary="Master project → subcontract per discipline (9 disciplines)"
            accentColor="#0d6efd"
          >
            <p style={{ margin: '0 0 1rem 0', color: '#6c757d', fontSize: '0.85rem' }}>
              A master project spawns subcontract jobs per discipline. Each subcontract = project number + discipline acronym.
            </p>
            <ConnectorDiagram
              masterLabel="#####"
              masterSub="MASTER PROJECT"
              items={multiItems}
            />
          </CollapsibleSection>

          <CollapsibleSection
            title="Operation Code Usage per Subcontract"
            summary="Each subcontract uses its discipline's operation code range"
            accentColor="#0a58ca"
          >
            <div style={{ backgroundColor: '#f0f4ff', borderRadius: '8px', padding: '1rem', border: '1px solid #c5d5f7' }}>
              <p style={{ margin: 0, color: '#3b6dcc', fontSize: '0.85rem', lineHeight: '1.5' }}>
                Subcontract <strong>71665PIP</strong> (Piping) → operation codes <strong>21000–21999</strong> for labour, <strong>81000</strong> for extras.<br />
                Subcontract <strong>71665TNK</strong> (Tank) → operation codes <strong>28000–28999</strong> for labour, <strong>88000</strong> for extras.
              </p>
            </div>
          </CollapsibleSection>
        </div>
      ) : (
        <CollapsibleSection
          title="Time & Material Project"
          summary="9 activity codes covering all cost types — no discipline ranges"
          accentColor="#0891b2"
        >
          <p style={{ margin: '0 0 1rem 0', color: '#6c757d', fontSize: '0.85rem' }}>
            T&M projects use a simplified code set — 9 activity codes covering all cost types. No discipline-specific ranges.
          </p>

          <div style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{
              padding: '20px 24px', borderRadius: '10px', backgroundColor: '#0891b2',
              color: 'white', fontWeight: 700, textAlign: 'center', flexShrink: 0,
              boxShadow: '0 4px 12px rgba(8,145,178,0.25)',
            }}>
              <div style={{ fontSize: '0.7rem', opacity: 0.8, marginBottom: '3px' }}>PROJECT</div>
              <div style={{ ...mono, fontSize: '1.2rem' }}>T#####</div>
            </div>

            <div style={{ flex: 1, minWidth: '280px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '0.7rem', color: '#6c757d', letterSpacing: '0.05em' }}>DESCRIPTION</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', borderBottom: '2px solid #dee2e6', fontSize: '0.7rem', color: '#6c757d', letterSpacing: '0.05em' }}>CODE</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', borderBottom: '2px solid #dee2e6', fontSize: '0.7rem', color: '#6c757d', letterSpacing: '0.05em' }}>TYPE</th>
                  </tr>
                </thead>
                <tbody>
                  {TM_CODES.map(c => {
                    const ct = COST_TYPES[c.costType];
                    return (
                      <tr key={c.code}>
                        <td style={{ padding: '7px 12px', borderBottom: '1px solid #f5f5f5', color: '#333' }}>{c.description}</td>
                        <td style={{ padding: '7px 12px', borderBottom: '1px solid #f5f5f5', textAlign: 'center', ...mono, fontSize: '0.82rem', color: '#495057' }}>{c.code}</td>
                        <td style={{ padding: '7px 12px', borderBottom: '1px solid #f5f5f5', textAlign: 'center' }}>
                          {ct && <span style={chip(ct.color + '18', ct.color)}>{ct.letter}</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </CollapsibleSection>
      )}
    </div>
  );
};

// ─── TAB 6: PROJECT EXAMPLES (collapsible + SVG) ───

const ProjectExamplesTab = () => {
  const [selected, setSelected] = useState('iko');
  const example = PROJECT_EXAMPLES.find(e => e.id === selected);

  const items = example ? example.disciplines.map(did => {
    const disc = DISCIPLINES.find(d => d.id === did);
    return {
      id: did,
      code: `${example.projectNumber}${did}`,
      name: disc?.name || did,
      color: disc?.color || '#6c757d',
    };
  }) : [];

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {PROJECT_EXAMPLES.map(ex => (
          <button key={ex.id} onClick={() => setSelected(ex.id)} style={pillBtn(selected === ex.id)}>
            {ex.clientCode} — {ex.clientName}
          </button>
        ))}
      </div>

      {example && (
        <CollapsibleSection
          title={`${example.projectNumber} — ${example.projectName}`}
          summary={`${example.type} · ${example.disciplines.length} discipline${example.disciplines.length !== 1 ? 's' : ''}`}
          accentColor={example.type === 'Single Discipline' ? '#0891b2' : '#7c3aed'}
        >
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.8rem', color: '#6c757d', marginBottom: '2px' }}>
              {example.projectNumber} = {example.clientCode} · {example.clientName}
            </div>
            <span style={{
              ...chip(example.type === 'Single Discipline' ? '#0891b2' : '#7c3aed', '#fff'),
              fontSize: '0.8rem', padding: '3px 10px',
            }}>
              {example.type}
            </span>
          </div>

          <ConnectorDiagram
            masterLabel={example.projectNumber}
            masterSub="MASTER PROJECT"
            items={items}
          />
        </CollapsibleSection>
      )}
    </div>
  );
};

// ─── HELP MODAL ───

const HelpModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const S = ({ title, num, children }) => (
    <div style={{ marginBottom: '1.75rem' }}>
      <h3 style={{ margin: '0 0 0.5rem 0', color: '#212529', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: '22px', height: '22px', borderRadius: '50%', backgroundColor: '#dc3545',
          color: 'white', fontSize: '0.7rem', fontWeight: 700, flexShrink: 0,
        }}>{num}</span>
        {title}
      </h3>
      <div style={{ paddingLeft: '30px' }}>{children}</div>
    </div>
  );

  return (
    <div onClick={onClose} style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 10000,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        backgroundColor: 'white', borderRadius: '16px', width: '90%',
        maxWidth: '780px', maxHeight: '85vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
      }}>
        <div style={{
          padding: '1.25rem 1.5rem', borderBottom: '1px solid #e9ecef',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
        }}>
          <h2 style={{ margin: 0, color: '#212529', fontSize: '1.2rem' }}>LGL Coding Structure — Complete Guide</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ListenButton compact guideId="coding-structure-help" title="Coding Structure Help" accent="#dc3545" />
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6c757d', lineHeight: 1 }}>×</button>
          </div>
        </div>

        <div style={{ padding: '1.5rem', fontSize: '0.88rem', lineHeight: '1.65', color: '#495057', overflowY: 'auto', flex: 1 }}>
          <HelpWithVideoTour guideId="coding-structure-help" title="Coding Structure Help" accent="#dc3545">
          <S num="1" title="Operation Codes (5-digit)">
            <p style={{ margin: '0 0 0.5rem 0' }}>Every cost in Maestro is tracked with a 5-digit operation code. The first 2 digits determine the category:</p>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <tbody>
                {[
                  ['00xxx', 'Contract Expenses — Material, Subs, Rentals, I/C, Regulatory, Site'],
                  ['01xxx', 'Indirect Labour — Support Staff, Supervisory, Other Direct'],
                  ['02xxx', 'Direct Labour — General / Miscellaneous'],
                  ['21–29xxx', 'Discipline Direct Labour — one range per discipline'],
                  ['81–89xxx', 'Extras / Change Orders — mirrors disciplines, +60k offset'],
                  ['85xxx', 'USA Intercompany — I/C Interim Reconciliations'],
                  ['90xxx', 'Revenues — contract billings to clients'],
                ].map(([r, d]) => (
                  <tr key={r}>
                    <td style={{ padding: '3px 8px', ...mono, fontSize: '0.78rem', color: '#6c757d', width: '75px' }}>{r}</td>
                    <td style={{ padding: '3px 8px' }}>{d}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </S>

          <S num="2" title="Code Hierarchy: Section → Phase → Activity">
            <p style={{ margin: 0 }}>
              <strong>Sections</strong> are rare top-level containers (only I/C Interim). <strong>Phases</strong> are groups written in ALL-CAPS ("SUPPORT STAFF", "PIPING DIRECT LABOUR"). <strong>Activities</strong> are where transactions actually post ("Project Manager", "Material", "Revenue").
            </p>
          </S>

          <S num="3" title="Cost Type Letters">
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 12px', fontSize: '0.82rem' }}>
              {Object.values(COST_TYPES).map(ct => (
                <React.Fragment key={ct.letter}>
                  <span style={{ ...chip(ct.color + '18', ct.color), fontSize: '0.7rem', justifySelf: 'start' }}>{ct.letter}</span>
                  <span><strong>{ct.name}</strong> — {ct.description}</span>
                </React.Fragment>
              ))}
            </div>
            <div style={{ marginTop: '0.5rem', fontSize: '0.78rem', color: '#6c757d' }}>
              "S" is shared by Subcontracts, Intercompany (USA), and Regulatory Inspection Fees.
            </div>
          </S>

          <S num="4" title="Disciplines and the +60,000 Pattern">
            <p style={{ margin: '0 0 0.4rem 0' }}>9 disciplines with 3-letter acronyms. Extras = Labour + 60,000:</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '2px', fontSize: '0.82rem' }}>
              {DISCIPLINES.map(d => (
                <span key={d.id}><strong style={{ color: d.color }}>{d.id}</strong> {d.name} · {d.labourRange.split('-')[0]}/{d.extrasCode}</span>
              ))}
            </div>
          </S>

          <S num="5" title="Project Structure">
            <p style={{ margin: '0 0 0.4rem 0' }}>
              <strong>Multi-Discipline:</strong> Master project (71665) → subcontracts per discipline (71665PIP, 71665MWR, etc.). Each subcontract uses its discipline's operation code range.
            </p>
            <p style={{ margin: 0 }}>
              <strong>T&M:</strong> 9 activity codes only (00102–90001). No discipline-specific ranges.
            </p>
          </S>

          <S num="6" title="Department Codes">
            <p style={{ margin: 0 }}>
              <strong>C</strong> (Canada) or <strong>U</strong> (USA) + 2-letter province/state + customer name. Example: <strong>CONBASF</strong> = Canada, Ontario, BASF Canada Inc.
            </p>
          </S>

          <S num="7" title="Template Selection">
            <p style={{ margin: 0 }}>
              <strong>Large MD</strong> — full support staff, all 9 disciplines (96 codes). <strong>Small MD</strong> — fewer support roles (58). <strong>Pipe Fab</strong> — pipe fab only (39). <strong>Tank Shop</strong> — detailed tank activities (46). All include T&M and USA IC sheets except Tank Shop.
            </p>
          </S>

          <S num="8" title="How This Feeds Other Modules">
            <p style={{ margin: 0 }}>
              <strong>Forecasting:</strong> Maestro imports map to these operation codes. <strong>Change Management:</strong> extras 81–89xxx track change orders by discipline. <strong>Cost Breakdown:</strong> cost types L/M/S/E/O/R align with estimation categories.
            </p>
          </S>
          </HelpWithVideoTour>
        </div>

        <div style={{
          padding: '0.75rem 1.5rem', borderTop: '1px solid #e9ecef',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0,
        }}>
          <span style={{ fontSize: '0.75rem', color: '#adb5bd' }}>Source: 14 verified files (Oct 2024 / Feb 2026)</span>
          <button onClick={onClose} style={{
            padding: '7px 18px', backgroundColor: '#dc3545', color: 'white',
            border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem',
          }}>
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── MAIN COMPONENT ───

const CodingStructurePage = () => {
  const { viewers, isConnected, connectionState } = usePresence('codingStructure', 'global', true);
  const [activeTab, setActiveTab] = useState('operations');
  const [showHelp, setShowHelp] = useState(false);
  const [operationSearch, setOperationSearch] = useState('');

  const tabs = [
    { id: 'operations',  label: 'Operation Codes', icon: '📋' },
    { id: 'templates',   label: 'Templates',       icon: '📊' },
    { id: 'disciplines', label: 'Discipline Map',  icon: '🔧' },
    { id: 'departments', label: 'Dept Codes',      icon: '🏢' },
    { id: 'structure',   label: 'Project Structure', icon: '🏗' },
    { id: 'examples',    label: 'Examples',         icon: '📄' },
  ];

  const renderTab = () => {
    switch (activeTab) {
      case 'operations':  return <OperationCodesTab search={operationSearch} />;
      case 'templates':   return <TemplatesTab />;
      case 'disciplines': return <DisciplineMapTab />;
      case 'departments': return <DepartmentCodesTab />;
      case 'structure':   return <ProjectStructureTab />;
      case 'examples':    return <ProjectExamplesTab />;
      default:            return <OperationCodesTab />;
    }
  };

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', backgroundColor: '#f8f9fa', minHeight: '100%' }}>
      {/* ── STICKY HEADER + TABS ── */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 100,
        backgroundColor: '#f8f9fa',
        padding: '1rem 1.5rem 0 1.5rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      }}>
        {/* Header */}
        <div style={{
          marginBottom: '0.5rem', backgroundColor: 'white', borderRadius: '10px',
          padding: '0.75rem 1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem',
        }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.25rem', color: '#212529', fontWeight: 700 }}>
              LGL Coding Structure
            </h1>
            <p style={{ margin: '0.15rem 0 0 0', color: '#6c757d', fontSize: '0.78rem' }}>
              Maestro operation codes, templates, disciplines, and project structure
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />
            <button
              onClick={() => setShowHelp(true)}
              style={{
                padding: '6px 14px', backgroundColor: '#f8f9fa', color: '#495057',
                border: '1px solid #dee2e6', borderRadius: '6px', cursor: 'pointer',
                fontWeight: 600, fontSize: '0.82rem', transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#e9ecef'; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#f8f9fa'; }}
            >
              Help
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{
          display: 'flex', gap: '3px', flexWrap: 'wrap',
          backgroundColor: 'white', padding: '4px', borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)', marginBottom: '0.75rem',
        }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '7px 13px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                fontWeight: activeTab === tab.id ? 600 : 500, fontSize: '0.85rem',
                backgroundColor: activeTab === tab.id ? '#dc3545' : 'transparent',
                color: activeTab === tab.id ? 'white' : '#495057',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { if (activeTab !== tab.id) e.currentTarget.style.backgroundColor = '#f8f9fa'; }}
              onMouseLeave={e => { if (activeTab !== tab.id) e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <span style={{ marginRight: '4px' }}>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search + Legend (frozen, only on Operation Codes tab) */}
        {activeTab === 'operations' && (
          <div>
            <div style={{ marginBottom: '0.4rem', position: 'relative' }}>
              <input
                type="text"
                placeholder="Search codes or descriptions..."
                value={operationSearch}
                onChange={e => setOperationSearch(e.target.value)}
                style={{
                  width: '100%', padding: '8px 14px 8px 34px', borderRadius: '8px',
                  border: '1px solid #dee2e6', fontSize: '0.85rem', outline: 'none',
                  backgroundColor: 'white', boxSizing: 'border-box',
                }}
              />
              <span style={{ position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)', color: '#adb5bd', fontSize: '0.85rem' }}>
                &#128269;
              </span>
              {operationSearch && (
                <button
                  onClick={() => setOperationSearch('')}
                  style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#adb5bd', fontSize: '1rem' }}
                >×</button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.5rem', fontSize: '0.65rem', color: '#adb5bd', flexWrap: 'wrap', paddingLeft: '2px' }}>
              <span>TMPL: 1=Large MD, 2=Small MD, 3=Pipe Fab, 4=Tank</span>
              <span>|</span>
              {Object.values(COST_TYPES).map(ct => (
                <span key={ct.letter} style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                  <span style={{ ...chip(ct.color + '18', ct.color), fontSize: '0.55rem', padding: '0 4px', marginRight: 0 }}>{ct.letter}</span>
                  <span>{ct.name}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} />

      {/* ── TAB CONTENT ── */}
      <div style={{ padding: '0 1.5rem 1.5rem 1.5rem' }}>
        {renderTab()}
      </div>
    </div>
  );
};

export default CodingStructurePage;
